import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FiUpload, FiDownload } from "react-icons/fi";
import { FaFileAlt } from "react-icons/fa";
import { BiLoader } from "react-icons/bi";
import toast from "react-hot-toast";
import clsx from "clsx";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";

interface IGenericBulkUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<{ status: boolean; message?: string; data?: any }>;
  onSuccess: (response?: any) => void;
  title?: string;
  sampleDownloadUrl?: string;
  sampleDownloadLabel?: string;
  allowedExtensions?: string[];
}

const GenericBulkUploadModal: React.FC<IGenericBulkUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  onSuccess,
  title = "Bulk Upload File",
  sampleDownloadUrl,
  sampleDownloadLabel = "Download Sample File",
  allowedExtensions = ["csv", "xlsx", "xls"],
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (selectedFile: File | undefined) => {
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();

    if (fileExtension && allowedExtensions.includes(fileExtension)) {
      setFile(selectedFile);
    } else {
      toast.error(`Please select a valid file (${allowedExtensions.map((ext) => `.${ext}`).join(", ")})`);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const selectedFile = event.dataTransfer.files?.[0];
    validateAndSetFile(selectedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    try {
      const response = await onUpload(file);
      setIsUploading(false);

      if (response?.status) {
        setFile(null);
        onSuccess(response?.data);
        onClose();
      }
    } catch (error) {
      setIsUploading(false);
      toast.error("An error occurred during upload");
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4 block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                    disabled={isUploading}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 border-b pb-3">
                      {title}
                    </Dialog.Title>

                    <div className="mt-4">
                      {file ? (
                        <div className="flex justify-between items-center border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center gap-3">
                            <FaFileAlt className="text-blue-500 h-8 w-8" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="text-xs font-semibold text-red-600 hover:text-red-500"
                            onClick={() => setFile(null)}
                            disabled={isUploading}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div
                          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                            dragOver ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400 bg-white"
                          }`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                        >
                          <FiUpload className="h-10 w-10 text-gray-400 mb-3" />
                          <label className="text-sm font-semibold text-blue-600 hover:text-blue-500 cursor-pointer">
                            Upload File
                            <input
                              type="file"
                              accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
                              className="hidden"
                              onChange={handleFileChange}
                            />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">Or drag & drop here</p>
                          <p className="text-xs text-gray-400 mt-2">
                            Supported formats: {allowedExtensions.map((ext) => ext.toUpperCase()).join(", ")}
                          </p>
                        </div>
                      )}
                    </div>

                    {sampleDownloadUrl && (
                      <div className="mt-5 flex items-center justify-between bg-gray-50 rounded-lg p-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <FiDownload className="h-4 w-4" />
                          <span>Need a template?</span>
                        </div>
                        <a
                          href={sampleDownloadUrl}
                          download={sampleDownloadUrl.split("/").pop()}
                          className="font-semibold text-blue-600 hover:text-blue-500 underline"
                        >
                          {sampleDownloadLabel}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex flex-row-reverse gap-3 border-t pt-4">
                  <button
                    type="button"
                    className={clsx(PRIMARY_BUTTON, "w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed")}
                    onClick={handleUpload}
                    disabled={!file || isUploading}
                  >
                    {isUploading ? (
                      <>
                        <BiLoader className="h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      "Upload File"
                    )}
                  </button>
                  <button
                    type="button"
                    className={clsx(SECONDARY_BUTTON, "mt-3 w-full sm:mt-0 sm:w-auto disabled:opacity-50")}
                    onClick={onClose}
                    disabled={isUploading}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default GenericBulkUploadModal;
